import { useCallback, useMemo } from "react";
import { asNumber } from "../../lib/kernelFormat.js";
import { CanvasSurface, KernelEmpty, resolveCssColor, colorForKey, fillStipplePath } from "./shared.jsx";

export const kind = "sankey";

// Layered sankey diagram. Nodes are assigned to layers by longest-path
// from any source-only node; within each layer they're stacked top-to-
// bottom by descending weight; links are bezier curves between
// per-flow anchor segments. Cycles are broken greedily — kernels
// passing cyclic flows will see the offending edge dropped.
export function Component({ spec, data, fillHeight = false }) {
    const model = useMemo(() => buildModel(spec, data), [spec, data]);
    const height = Math.max(200, Math.min(500, Number(spec.height) || 320));

    const draw = useCallback((ctx, { width, height: h, root }) => {
        if (!model) return;
        const { layers, links } = model;
        if (!layers.length) return;
        const ink = resolveCssColor(root, "var(--ink)") || "#fff";
        const muted = resolveCssColor(root, "var(--plot-ink-soft)") || "#888";

        const padL = 8;
        const padR = 8;
        const padT = 14;
        const padB = 14;
        const gx = Math.max(1, width - padL - padR);
        const gy = Math.max(1, h - padT - padB);
        const nodeW = 10;
        const xStep = layers.length > 1 ? (gx - nodeW) / (layers.length - 1) : 0;

        // Compute per-layer total weight → scale per pixel.
        const maxLayerWeight = layers.reduce((m, layer) => {
            const sum = layer.reduce((s, n) => s + Math.max(n.inWeight, n.outWeight), 0);
            return Math.max(m, sum);
        }, 0) || 1;
        const padPerNode = 4;
        const pxPerWeight = (gy - padPerNode * 6) / maxLayerWeight;

        // Layout: stack nodes per layer
        const nodePos = new Map();
        for (let li = 0; li < layers.length; li++) {
            const layer = layers[li];
            let y = padT;
            const x = padL + li * xStep;
            for (const node of layer) {
                const wt = Math.max(node.inWeight, node.outWeight);
                const nh = Math.max(2, wt * pxPerWeight);
                nodePos.set(node.id, { x, y, w: nodeW, h: nh });
                y += nh + padPerNode;
            }
        }

        // Sub-allocate per-flow anchor positions on each node's side.
        const flowAnchors = new Map();
        for (const layer of layers) {
            for (const node of layer) {
                const pos = nodePos.get(node.id);
                if (!pos) continue;
                // outgoing — anchored on right edge
                const outs = links.filter((l) => l.source === node.id).sort((a, b) => a.targetIndex - b.targetIndex);
                let yo = pos.y;
                for (const l of outs) {
                    const lh = Math.max(1, l.value * pxPerWeight);
                    flowAnchors.set(`${l.id}:source`, { x: pos.x + pos.w, y0: yo, y1: yo + lh });
                    yo += lh;
                }
                // incoming — anchored on left edge
                const ins = links.filter((l) => l.target === node.id).sort((a, b) => a.sourceIndex - b.sourceIndex);
                let yi = pos.y;
                for (const l of ins) {
                    const lh = Math.max(1, l.value * pxPerWeight);
                    flowAnchors.set(`${l.id}:target`, { x: pos.x, y0: yi, y1: yi + lh });
                    yi += lh;
                }
            }
        }

        // Draw links as bezier ribbons — stippled fill so the texture
        // matches the rest of the dashboard's filled surfaces.
        for (const link of links) {
            const src = flowAnchors.get(`${link.id}:source`);
            const tgt = flowAnchors.get(`${link.id}:target`);
            if (!src || !tgt) continue;
            const c = resolveCssColor(root, link.color);
            const midX = (src.x + tgt.x) / 2;
            const traceRibbon = () => {
                ctx.beginPath();
                ctx.moveTo(src.x, src.y0);
                ctx.bezierCurveTo(midX, src.y0, midX, tgt.y0, tgt.x, tgt.y0);
                ctx.lineTo(tgt.x, tgt.y1);
                ctx.bezierCurveTo(midX, tgt.y1, midX, src.y1, src.x, src.y1);
                ctx.closePath();
            };
            const minY = Math.min(src.y0, tgt.y0);
            const maxY = Math.max(src.y1, tgt.y1);
            const bbox = { left: src.x, top: minY, right: tgt.x, bottom: maxY };
            traceRibbon();
            fillStipplePath(ctx, bbox, c, { density: 0.55 });
        }

        // Draw nodes
        ctx.fillStyle = ink;
        for (const layer of layers) {
            for (const node of layer) {
                const pos = nodePos.get(node.id);
                if (!pos) continue;
                const c = resolveCssColor(root, node.color);
                ctx.fillStyle = c;
                ctx.fillRect(pos.x, pos.y, pos.w, pos.h);
            }
        }

        // Labels
        ctx.font = "10px ui-monospace, SFMono-Regular, Menlo, monospace";
        ctx.textBaseline = "middle";
        for (let li = 0; li < layers.length; li++) {
            const layer = layers[li];
            const isLast = li === layers.length - 1;
            for (const node of layer) {
                const pos = nodePos.get(node.id);
                if (!pos) continue;
                ctx.fillStyle = ink;
                if (isLast) {
                    ctx.textAlign = "right";
                    ctx.fillText(truncate(node.name, 16), pos.x - 4, pos.y + pos.h / 2);
                } else {
                    ctx.textAlign = "left";
                    ctx.fillText(truncate(node.name, 16), pos.x + pos.w + 4, pos.y + pos.h / 2);
                }
            }
        }
        void muted;
    }, [model]);

    if (!model || !model.links.length) {
        return <KernelEmpty message={spec.empty || "no flows"} />;
    }

    return (
        <div className={`rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[10px] py-[8px]${fillHeight ? " flex h-full min-h-0 flex-col" : ""}`}>
            <CanvasSurface
                draw={draw}
                deps={[model]}
                height={fillHeight ? null : height}
                className={`w-full bg-[color:var(--plot-bg)] rounded-[2px] border border-[color:var(--line-soft)]${fillHeight ? " flex-1 min-h-0" : ""}`}
            />
        </div>
    );
}

function buildModel(spec, data) {
    const arr = Array.isArray(data) ? data : [];
    const nodeMap = new Map();
    const rawLinks = [];

    const ensureNode = (name) => {
        if (!nodeMap.has(name)) {
            nodeMap.set(name, {
                id: name,
                name,
                color: spec.nodeColor || colorForKey(name),
                inWeight: 0,
                outWeight: 0,
                outs: [],
                ins: [],
            });
        }
        return nodeMap.get(name);
    };

    for (let i = 0; i < arr.length; i++) {
        const row = arr[i];
        if (!row) continue;
        const s = row[spec.sourceField];
        const t = row[spec.targetField];
        const v = asNumber(row[spec.valueField]) ?? 0;
        if (s == null || t == null || v <= 0 || s === t) continue;
        const sName = String(s);
        const tName = String(t);
        const sNode = ensureNode(sName);
        const tNode = ensureNode(tName);
        const link = {
            id: `l${i}`,
            source: sName,
            target: tName,
            value: v,
            color: spec.nodeColor || colorForKey(sName),
        };
        rawLinks.push(link);
        sNode.outs.push(link);
        tNode.ins.push(link);
        sNode.outWeight += v;
        tNode.inWeight += v;
    }

    if (!rawLinks.length) return { layers: [], links: [] };

    // Layer assignment via longest-path-from-source. Break cycles
    // greedily during traversal (skip back-edges).
    const layerOf = new Map();
    const visiting = new Set();
    const visited = new Set();
    const acceptedLinks = [];
    const acceptedById = new Set();

    function visit(nodeName, depth) {
        if (visited.has(nodeName)) {
            layerOf.set(nodeName, Math.max(layerOf.get(nodeName) ?? 0, depth));
            return;
        }
        if (visiting.has(nodeName)) {
            // cycle — caller should drop this edge
            return false;
        }
        visiting.add(nodeName);
        layerOf.set(nodeName, Math.max(layerOf.get(nodeName) ?? 0, depth));
        const node = nodeMap.get(nodeName);
        for (const out of node.outs) {
            const ok = visit(out.target, depth + 1);
            if (ok === false) continue; // drop back-edge
            acceptedById.add(out.id);
        }
        visiting.delete(nodeName);
        visited.add(nodeName);
        return true;
    }

    // Seed from source-only nodes; if every node has incoming edges
    // (pure cycle), seed from the lowest-in-degree.
    const sources = [...nodeMap.values()].filter((n) => n.ins.length === 0);
    if (sources.length) {
        for (const s of sources) visit(s.id, 0);
    } else {
        const seed = [...nodeMap.values()].sort((a, b) => a.ins.length - b.ins.length)[0];
        if (seed) visit(seed.id, 0);
    }
    // catch any unvisited
    for (const n of nodeMap.values()) {
        if (!visited.has(n.id)) visit(n.id, layerOf.get(n.id) ?? 0);
    }

    // Filter links to accepted ones (drops back-edges)
    for (const l of rawLinks) {
        if (acceptedById.has(l.id)) {
            acceptedLinks.push(l);
        } else if ((layerOf.get(l.source) ?? 0) < (layerOf.get(l.target) ?? 0)) {
            acceptedLinks.push(l);
        }
    }

    // Recompute weights using accepted links only
    for (const n of nodeMap.values()) {
        n.inWeight = 0;
        n.outWeight = 0;
        n.outs = [];
        n.ins = [];
    }
    for (const l of acceptedLinks) {
        const sNode = nodeMap.get(l.source);
        const tNode = nodeMap.get(l.target);
        sNode.outWeight += l.value;
        tNode.inWeight += l.value;
        sNode.outs.push(l);
        tNode.ins.push(l);
    }

    // Build layers (array of arrays, sorted within by descending weight)
    const maxLayer = Math.max(...layerOf.values());
    const layers = [];
    for (let i = 0; i <= maxLayer; i++) layers.push([]);
    for (const n of nodeMap.values()) {
        const li = layerOf.get(n.id) ?? 0;
        layers[li].push(n);
    }
    for (const layer of layers) {
        layer.sort((a, b) => Math.max(b.inWeight, b.outWeight) - Math.max(a.inWeight, a.outWeight));
    }
    // Indices into layers help with stable link ordering on each node side
    const indexOf = new Map();
    for (const layer of layers) {
        for (let i = 0; i < layer.length; i++) indexOf.set(layer[i].id, i);
    }
    for (const l of acceptedLinks) {
        l.sourceIndex = indexOf.get(l.source) ?? 0;
        l.targetIndex = indexOf.get(l.target) ?? 0;
    }

    return { layers, links: acceptedLinks };
}

function truncate(s, n) {
    if (s.length > n) return s.slice(0, n - 1) + "…";
    return s;
}

function withAlpha(color, alpha) {
    if (!color) return color;
    if (color.startsWith("rgba(")) {
        return color.replace(/rgba\(([^)]+)\)/, (m, inner) => {
            const parts = inner.split(",").map((s) => s.trim());
            parts[3] = String(alpha);
            return `rgba(${parts.join(", ")})`;
        });
    }
    if (color.startsWith("rgb(")) {
        return color.replace("rgb(", "rgba(").replace(")", `, ${alpha})`);
    }
    if (color.startsWith("#")) {
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        if (Number.isFinite(r) && Number.isFinite(g) && Number.isFinite(b)) {
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }
    }
    return color;
}
